import { NavLink, useLocation, useNavigate } from "react-router";
import { useInfiniteQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { api, authClient, unwrap } from "../api.ts";
import { useBrand } from "../hooks.ts";
import { ADMIN_GROUPS } from "../lib/admin-nav.ts";
import { relative } from "../lib/cron.ts";
import { cx, Dialog, Spinner } from "./ui.tsx";
import { Icon, type IconName } from "./icons.tsx";

/**
 * The menu, laid out the way people know from ChatGPT, Claude and Gemini: new chat and search at the top,
 * the pages, then recent chats; your name at the bottom opens Settings and Sign out.
 */
type NavItem = { to: string; label: string; icon: IconName };
const ADMIN_ICONS: Record<string, IconName> = { people: "people", knowledge: "knowledge", assistants: "assistants", workspace: "workspace" };
const PAGES: NavItem[] = [
  { to: "/approvals", label: "Approvals", icon: "approvals" },
  { to: "/dashboard", label: "Dashboard", icon: "dashboard" },
  { to: "/goals", label: "Goals", icon: "goals" },
  { to: "/schedules", label: "Schedules", icon: "schedules" },
  { to: "/files", label: "Files", icon: "files" },
  { to: "/web-apps", label: "Web apps", icon: "webApps" },
  { to: "/memory", label: "Memory", icon: "memory" },
  { to: "/skills", label: "Skills", icon: "skills" },
  { to: "/connections", label: "Connected apps", icon: "connections" },
];
const ADMIN: NavItem[] = ADMIN_GROUPS.map((g) => ({ to: `/admin/${g.base}`, label: g.label, icon: ADMIN_ICONS[g.base] ?? "settings" }));

const SESSION_PAGE = 30;
type Session = { id: string; title: string; source: string; lastActive: number };

/** Recent chats, a page at a time (the Chat page refreshes the same list after each reply). */
export function useChatSessions() {
  const q = useInfiniteQuery({
    queryKey: ["chat-sessions"],
    queryFn: ({ pageParam }) => unwrap(api.me.assistant.sessions.$get({ query: { limit: String(SESSION_PAGE), offset: String(pageParam) } })),
    initialPageParam: 0,
    getNextPageParam: (last, all) => (last.hasMore ? all.length * SESSION_PAGE : undefined),
    refetchInterval: 30_000,
    retry: false,
  });
  const list = useMemo(() => {
    const seen = new Set<string>();
    return (q.data?.pages ?? []).flatMap((p) => p.sessions).filter((s) => !seen.has(s.id) && !!seen.add(s.id));
  }, [q.data]);
  return { q, list };
}

/** The logo on the dark menu: Mnemio's white logo, the install's own, or its name. Folded, just the mark. */
export function MenuLogo({ compact }: { compact?: boolean }) {
  const b = useBrand();
  if (b.loading) return <span className="inline-block h-5" />;
  if (b.logoUrl) return <img src={b.logoUrl} alt={b.name} className={cx("h-5 w-auto max-w-full select-none object-contain", compact && "max-w-7")} draggable={false} />;
  if (b.custom) return <span className="truncate font-display text-[17px] font-semibold tracking-tight text-white">{compact ? b.name[0] : b.name}</span>;
  return <img src={compact ? "/mnemio-mark-white.svg" : "/mnemio-logo.svg"} alt={b.name} className="h-[18px] w-auto select-none" draggable={false} />;
}

const row = "group relative flex h-9 w-full items-center gap-3 whitespace-nowrap rounded-[10px] px-2.5 text-[13.5px] transition-colors";
const rowIdle = "text-[var(--chrome-text)] hover:bg-white/[0.05] hover:text-white";
const rowActive = "bg-white/[0.08] font-medium text-white";

export function Sidebar({
  user,
  admin,
  webApps,
  pending,
  busy,
  collapsed,
  onCollapse,
  onClose,
}: {
  user: { name: string; email: string; image?: string | null };
  admin: boolean;
  webApps: boolean;
  pending: number;
  /** A reply is streaming: switching chats would cut it off. */
  busy: boolean;
  collapsed: boolean;
  /** Desktop only: fold the menu to a rail of icons (or open it again). */
  onCollapse?: () => void;
  /** Phones: close the drawer. */
  onClose?: () => void;
}) {
  const navigate = useNavigate();
  const loc = useLocation();
  const [searching, setSearching] = useState(false);
  const [adminOpen, setAdminOpen] = useState(() => loc.pathname.startsWith("/admin"));
  useEffect(() => {
    if (loc.pathname.startsWith("/admin")) setAdminOpen(true);
  }, [loc.pathname]);
  const { q, list } = useChatSessions();

  const tip = (label: string) => (collapsed ? { title: label, "aria-label": label } : {});
  const link = (i: NavItem, indent?: boolean) => (
    <NavLink key={i.to} to={i.to} {...tip(i.label)} className={({ isActive }) => cx(row, isActive ? rowActive : rowIdle, collapsed && "justify-center px-0", indent && !collapsed && "pl-9")}>
      {!indent && <Icon name={i.icon} size={17} className="shrink-0 opacity-80 group-aria-[current=page]:text-[var(--color-brand)] group-aria-[current=page]:opacity-100" />}
      {!collapsed && <span className="truncate">{i.label}</span>}
      {i.to === "/approvals" && pending > 0 && (collapsed ? <span className="absolute top-1.5 right-2 h-2 w-2 rounded-full bg-[var(--color-brand)] ring-2 ring-[var(--chrome)]" /> : <CountPill n={pending} />)}
    </NavLink>
  );

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className={cx("flex h-14 shrink-0 items-center gap-2", collapsed ? "justify-center" : "justify-between pr-1 pl-3")}>
        {!collapsed && <NavLink to="/chat" className="min-w-0"><MenuLogo /></NavLink>}
        {onCollapse && (
          <button onClick={onCollapse} className="grid h-9 w-9 place-items-center rounded-[10px] text-white/55 transition-colors hover:bg-white/[0.06] hover:text-white" title={collapsed ? "Open menu" : "Close menu"} aria-label={collapsed ? "Open menu" : "Close menu"}>
            {collapsed ? <MenuLogo compact /> : <Icon name="panel" size={18} />}
          </button>
        )}
        {onClose && (
          <button onClick={onClose} className="grid h-9 w-9 place-items-center rounded-[10px] text-white/55 hover:bg-white/[0.06] hover:text-white" aria-label="Close menu">
            <Icon name="close" size={18} />
          </button>
        )}
      </div>

      <div className={cx("min-h-0 flex-1 overflow-y-auto pb-3", collapsed ? "px-2" : "px-2.5")}>
        <div className="space-y-px">
          <button disabled={busy} onClick={() => navigate("/chat")} {...tip("New chat")} className={cx(row, rowIdle, "disabled:opacity-50", collapsed && "justify-center px-0")}>
            <Icon name="newChat" size={17} className="shrink-0 opacity-75" />
            {!collapsed && <span>New chat</span>}
          </button>
          <button onClick={() => setSearching(true)} {...tip("Search chats")} className={cx(row, rowIdle, collapsed && "justify-center px-0")}>
            <Icon name="search" size={17} className="shrink-0 opacity-75" />
            {!collapsed && <span>Search chats</span>}
          </button>
        </div>

        <div className="mt-4 space-y-px">
          {PAGES.filter((i) => i.to !== "/web-apps" || webApps).map((i) => link(i))}
          {admin && (
            <>
              <button onClick={() => (collapsed ? navigate(ADMIN[0]!.to) : setAdminOpen((v) => !v))} {...tip("Admin")} aria-expanded={adminOpen} className={cx(row, rowIdle, collapsed && "justify-center px-0", collapsed && loc.pathname.startsWith("/admin") && rowActive)}>
                <Icon name="admin" size={17} className="shrink-0 opacity-75" />
                {!collapsed && (
                  <>
                    <span>Admin</span>
                    <Icon name="chevron" size={14} className={cx("ml-auto opacity-50 transition-transform duration-200", adminOpen && "rotate-90")} />
                  </>
                )}
              </button>
              {adminOpen && !collapsed && ADMIN.map((i) => link(i, true))}
            </>
          )}
        </div>

        {!collapsed && !q.error && (
          <Recents sessions={list} loading={q.isPending} busy={busy} more={q.hasNextPage ? { loading: q.isFetchingNextPage, load: () => void q.fetchNextPage() } : null} />
        )}
      </div>

      <Profile user={user} collapsed={collapsed} />
      <SearchChats open={searching} onClose={() => setSearching(false)} sessions={list} busy={busy} more={q.hasNextPage ? { loading: q.isFetchingNextPage, load: () => void q.fetchNextPage() } : null} />
    </div>
  );
}

type More = { loading: boolean; load: () => void } | null;

function Recents({ sessions, loading, busy, more }: { sessions: Session[]; loading: boolean; busy: boolean; more: More }) {
  const loc = useLocation();
  const activeId = loc.pathname.startsWith("/chat/") ? loc.pathname.slice(6) : undefined;
  const navigate = useNavigate();
  const qc = useQueryClient();
  const sentinel = useRef<HTMLDivElement>(null);
  // Loads older chats as the bottom of the list scrolls into view.
  useEffect(() => {
    const el = sentinel.current;
    if (!el || !more || more.loading) return;
    const io = new IntersectionObserver((e) => e[0]?.isIntersecting && more.load());
    io.observe(el);
    return () => io.disconnect();
  }, [more]);
  const rename = async (id: string, current: string) => {
    const title = prompt("Rename chat", current)?.trim();
    if (!title) return;
    await unwrap(api.me.assistant.sessions[":id"].$patch({ param: { id }, json: { title } })).catch((e: Error) => alert(e.message));
    void qc.invalidateQueries({ queryKey: ["chat-sessions"] });
  };
  const remove = async (id: string) => {
    if (!confirm("Delete this chat? This can't be undone.")) return;
    await unwrap(api.me.assistant.sessions[":id"].$delete({ param: { id } })).catch((e: Error) => alert(e.message));
    void qc.invalidateQueries({ queryKey: ["chat-sessions"] });
    if (id === activeId) navigate("/chat");
  };
  return (
    <div className="mt-5">
      <div className="px-2.5 pb-1 text-xs font-medium text-white/40">Recents</div>
      {loading && <div className="grid place-items-center py-6"><Spinner /></div>}
      {!loading && !sessions.length && <p className="px-2.5 py-2 text-[13px] text-white/40">Your chats will appear here.</p>}
      <div className="space-y-px">
        {sessions.map((s) => (
          <ChatRow key={s.id} s={s} active={s.id === activeId} busy={busy} onOpen={() => navigate(`/chat/${s.id}`)} onRename={() => void rename(s.id, s.title)} onDelete={() => void remove(s.id)} />
        ))}
      </div>
      {more && (
        <div ref={sentinel} className="py-2 text-center">
          <button onClick={more.load} disabled={more.loading} className="text-xs text-white/45 hover:text-white">{more.loading ? "Loading…" : "Load older chats"}</button>
        </div>
      )}
    </div>
  );
}

function ChatRow({ s, active, busy, onOpen, onRename, onDelete }: { s: Session; active: boolean; busy: boolean; onOpen: () => void; onRename: () => void; onDelete: () => void }) {
  const [menu, setMenu] = useState(false);
  return (
    <div className={cx("group relative flex h-9 items-center rounded-[10px] transition-colors", active ? rowActive : "hover:bg-white/[0.05]", menu && !active && "bg-white/[0.05]")}>
      <button disabled={busy && !active} onClick={onOpen} className={cx("min-w-0 flex-1 truncate px-2.5 text-left text-[13.5px]", active ? "text-white" : "text-[var(--chrome-text)] group-hover:text-white")} title={s.title}>
        {s.source === "cron" && <Icon name="clock" size={13} className="mr-1.5 -mt-0.5 inline opacity-50" />}
        {s.title}
      </button>
      <button
        onClick={() => setMenu((v) => !v)}
        aria-label={`Options for ${s.title}`}
        className={cx("mr-1 grid h-7 w-7 shrink-0 place-items-center rounded-lg text-white/50 hover:bg-white/[0.08] hover:text-white", menu || active ? "flex" : "hidden group-hover:grid")}
      >
        <Icon name="more" size={16} />
      </button>
      {menu && (
        <Popover onClose={() => setMenu(false)} className="top-full right-0 mt-1 w-40">
          <MenuItem icon="rename" onClick={() => { setMenu(false); onRename(); }}>Rename</MenuItem>
          <MenuItem icon="trash" danger onClick={() => { setMenu(false); onDelete(); }}>Delete</MenuItem>
        </Popover>
      )}
    </div>
  );
}

/** Your name at the bottom; it opens Settings and Sign out (the way ChatGPT and Claude do). */
function Profile({ user, collapsed }: { user: { name: string; email: string; image?: string | null }; collapsed: boolean }) {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const qc = useQueryClient();
  const avatar = user.image ? (
    <img src={user.image} alt="" className="h-8 w-8 shrink-0 rounded-full" />
  ) : (
    <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-gradient-to-br from-white/25 to-white/5 text-xs font-semibold text-white ring-1 ring-white/10">{(user.name || user.email)[0]?.toUpperCase()}</span>
  );
  return (
    <div className={cx("relative shrink-0 border-t border-white/[0.07] p-2", collapsed && "flex justify-center")}>
      <button onClick={() => setOpen((v) => !v)} aria-expanded={open} aria-label="Account" className={cx("flex items-center gap-2.5 rounded-xl text-left transition-colors hover:bg-white/[0.05]", collapsed ? "p-1" : "w-full p-2", open && "bg-white/[0.06]")}>
        {avatar}
        {!collapsed && (
          <span className="min-w-0 flex-1">
            <span className="block truncate text-[13.5px] font-medium text-white">{user.name}</span>
            <span className="block truncate text-xs text-[var(--chrome-text)]">{user.email}</span>
          </span>
        )}
      </button>
      {open && (
        <Popover onClose={() => setOpen(false)} className={cx("bottom-full mb-1", collapsed ? "left-2 w-60" : "inset-x-2")}>
          <div className="truncate px-2.5 pt-1.5 pb-2 text-xs text-white/45">{user.email}</div>
          <MenuItem icon="settings" onClick={() => { setOpen(false); navigate("/settings"); }}>Settings</MenuItem>
          <div className="my-1 h-px bg-white/10" />
          <MenuItem
            icon="signOut"
            onClick={async () => {
              await authClient.signOut();
              qc.clear();
              location.href = "/login";
            }}
          >
            Sign out
          </MenuItem>
        </Popover>
      )}
    </div>
  );
}

/** Find a chat by its title, like ChatGPT's search: type, then pick (or press Enter for the first). */
function SearchChats({ open, onClose, sessions, busy, more }: { open: boolean; onClose: () => void; sessions: Session[]; busy: boolean; more: More }) {
  const [text, setText] = useState("");
  const navigate = useNavigate();
  useEffect(() => {
    if (!open) setText("");
  }, [open]);
  const shown = text ? sessions.filter((s) => s.title.toLowerCase().includes(text.toLowerCase())) : sessions.slice(0, 12);
  const go = (to: string) => {
    if (busy) return;
    onClose();
    navigate(to);
  };
  return (
    <Dialog open={open} onClose={onClose} title="Search chats" wide>
      <div className="-mt-1 space-y-3">
        <div className="relative">
          <Icon name="search" size={16} className="pointer-events-none absolute top-1/2 left-3.5 -translate-y-1/2 text-[var(--muted)]" />
          <input
            autoFocus
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && shown[0] && go(`/chat/${shown[0].id}`)}
            placeholder="Search by title"
            className="h-11 w-full rounded-xl bg-[var(--panel-2)] pr-3 pl-10 text-sm outline-none transition-shadow focus:bg-[var(--panel)] focus:shadow-[0_0_0_1px_var(--color-accent),0_0_0_4px_color-mix(in_srgb,var(--color-brand)_22%,transparent)]"
          />
        </div>
        <div className="-mx-2 max-h-[50vh] overflow-y-auto">
          {!text && (
            <button onClick={() => go("/chat")} className="flex w-full items-center gap-3 rounded-[10px] px-2.5 py-2 text-left text-sm hover:bg-[var(--panel-2)]">
              <Icon name="newChat" size={16} className="opacity-70" /> New chat
            </button>
          )}
          {shown.map((s) => (
            <button key={s.id} onClick={() => go(`/chat/${s.id}`)} className="flex w-full items-center gap-3 rounded-[10px] px-2.5 py-2 text-left text-sm hover:bg-[var(--panel-2)]">
              <Icon name="chat" size={16} className="shrink-0 opacity-50" />
              <span className="min-w-0 flex-1 truncate">{s.title}</span>
              <span className="shrink-0 text-xs text-[var(--muted)]">{relative(new Date(s.lastActive * 1000).toISOString())}</span>
            </button>
          ))}
          {text && !shown.length && <p className="px-2.5 py-4 text-center text-sm text-[var(--muted)]">No chat title matches.{more ? " Older chats aren't loaded yet." : ""}</p>}
          {more && text && (
            <button onClick={more.load} disabled={more.loading} className="w-full py-2 text-center text-xs text-[var(--muted)] hover:text-[var(--text)]">{more.loading ? "Loading…" : "Search older chats too"}</button>
          )}
        </div>
      </div>
    </Dialog>
  );
}

/** A small floating menu; a click elsewhere or Esc closes it. */
function Popover({ onClose, className, children }: { onClose: () => void; className?: string; children: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const down = (e: PointerEvent) => !ref.current?.parentElement?.contains(e.target as Node) && onClose();
    const esc = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("pointerdown", down);
    window.addEventListener("keydown", esc);
    return () => {
      window.removeEventListener("pointerdown", down);
      window.removeEventListener("keydown", esc);
    };
  }, [onClose]);
  return (
    <div ref={ref} role="menu" className={cx("absolute z-40 rounded-2xl border border-white/10 bg-[#1f1f1e] p-1.5 text-white/90 shadow-[0_16px_40px_-12px_rgb(0_0_0/0.6)]", className)}>
      {children}
    </div>
  );
}

function MenuItem({ icon, danger, onClick, children }: { icon: IconName; danger?: boolean; onClick: () => void; children: ReactNode }) {
  return (
    <button role="menuitem" onClick={onClick} className={cx("flex w-full items-center gap-2.5 rounded-[10px] px-2.5 py-2 text-left text-[13.5px] transition-colors hover:bg-white/[0.07]", danger && "text-red-400")}>
      <Icon name={icon} size={16} className="shrink-0 opacity-70" />
      {children}
    </button>
  );
}

export function CountPill({ n }: { n: number }) {
  return <span className="ml-auto inline-grid h-[18px] min-w-[18px] place-items-center rounded-full bg-[var(--color-brand)] px-1.5 text-[10.5px] font-semibold text-[#0c2533] tabular-nums">{n}</span>;
}
